import { apiClient } from './client';

export type CardRole = 'employee' | 'employer';
/** `connect`: one known person, links on redeem, single-use. `request`: posted, each redeem files a link request. */
export type ShareMode = 'connect' | 'request';

export interface ShareCodeIssued {
  /** Formatted XXXXX-XXXXX */
  code: string;
  url: string;
  qrDataUrl: string;
  expiresAt: string;
  mode: ShareMode;
}

/** GET /profile-shares/mine — the live code of a mode, or what became of the last one. */
export interface ShareCodeMine {
  active: boolean;
  mode: ShareMode;
  expiresAt?: string;
  uses?: number;
  maxUses?: number;
  /** request mode: redeems the owner has not answered yet */
  pendingRequests?: number;
  createdAt?: string;
  /** no live code: the last one was redeemed by … */
  usedBy?: { name: string; at: string };
  revokedAt?: string;
  expiredAt?: string;
}

export interface ShareCandidate {
  id: string;
  kind: 'employee' | 'employerContact';
  name: string;
  status: string;
  emailMatches: boolean;
}

/** What the redeemer sees before confirming: the owner's card preview and where it could attach. */
export interface SharePreview {
  ownerRole: CardRole;
  mode: ShareMode;
  /** A request code carries the first name only */
  owner: { firstName: string; lastName: string };
  /** `data` is keyed by Employee path for an employee card (`payment.name`, `tax.cantonCode`, …)
   *  and by plain field for an employer card (`name`, `canton`, `serviceTypes`, …). */
  card: { data: Record<string, unknown> } | null;
  expiresAt: string;
  linkState: 'none' | 'pending' | 'accepted' | 'rejected' | 'unlinked';
  parties: { employerUserId: string; employeeUserId: string };
  candidates: ShareCandidate[];
}

export type SharePending = null | { expired: true } | ({ expired: false } & SharePreview);

/**
 * The terms only the household knows when it employs someone from their card; everything
 * else on the record comes from the card's profile (backend config/mirroredFields.js).
 */
export interface ShareEmployTerms {
  qualifications: { serviceType: string; hourlyRate: number }[];
  defaultServiceType?: string;
  estimatedHoursPerWeek: number;
  /** ISO YYYY-MM-DD */
  startDate?: string;
  paymentFrequency?: 'per-shift' | 'weekly' | 'monthly';
}

export interface ShareRedeemBody {
  employeeRecordId?: string;
  employerContactId?: string;
  /** Employ from the card in one step (employee card, connect mode, new record). */
  employ?: ShareEmployTerms;
}

/** A connect code links at once … */
export interface ShareLinkedResult {
  mode?: 'connect';
  link: { status: 'accepted'; via: 'code'; employerUserId: string; employeeUserId: string };
  employeeRecordId: string;
  employerContactId: string | null;
}
/** … a request code only files a pending request for the owner to accept. */
export interface ShareRequestedResult {
  mode: 'request';
  link: { status: 'pending'; via: 'code'; initiatedBy: CardRole; employerUserId: string; employeeUserId: string };
  ownerFirstName: string;
}
export type ShareRedeemResult = ShareLinkedResult | ShareRequestedResult;

type Envelope<T> = { success: boolean; data: T };

export const profileSharesApi = {
  create: (role: CardRole, mode: ShareMode = 'connect') =>
    apiClient.post<Envelope<ShareCodeIssued>>('/profile-shares', { role, mode }).then((r) => r.data),
  mine: (role: CardRole, mode: ShareMode = 'connect') =>
    apiClient.get<Envelope<ShareCodeMine>>(`/profile-shares/mine?role=${role}&mode=${mode}`).then((r) => r.data),
  revoke: (role: CardRole, mode: ShareMode = 'connect') =>
    apiClient.delete<Envelope<{ revoked: number }>>(`/profile-shares/mine?role=${role}&mode=${mode}`).then((r) => r.data),
  preview: (code: string) =>
    apiClient.get<Envelope<SharePreview>>(`/profile-shares/${encodeURIComponent(code)}/preview`).then((r) => r.data),
  redeem: (code: string, body: ShareRedeemBody = {}) =>
    apiClient
      .post<Envelope<ShareRedeemResult>>(`/profile-shares/${encodeURIComponent(code)}/redeem`, body)
      .then((r) => r.data),
  pending: () => apiClient.get<Envelope<SharePending>>('/profile-shares/pending').then((r) => r.data),
  redeemPending: (body: ShareRedeemBody = {}) =>
    apiClient.post<Envelope<ShareRedeemResult>>('/profile-shares/pending/redeem', body).then((r) => r.data),
};
